import React, { useState } from 'react';
import { addDays } from 'date-fns';
import { Casa } from '../../types';
import { useTareas } from '../../context/TareasContext';
import { usePlus } from '../../context/PlusContext';
import { TAREAS_PLANTILLA } from '../../config/tareasPlantilla';
import { describirRepeticion, primeraFecha } from '../../utils/tareas';
import { Check } from 'lucide-react';
import { MensajeError, ModalBase, botonPrincipal, botonSecundario, chip } from './ui';
import { vibrar } from '../../utils/hapticos';

/**
 * Tareas habituales para empezar: se marcan las que se quieren y se crean
 * sin asignar. Las que ya existen en la casa (mismo nombre) no se repiten.
 */
export const ModalPlantillaTareas: React.FC<{ casas: Casa[]; casaId: string; onClose: () => void }> =
  ({ casas, casaId: casaInicial, onClose }) => {
  const { tareas, crearTareas } = useTareas();
  const { ofrecerSiEsLimite } = usePlus();
  const [casaId, setCasaId] = useState(casaInicial);
  const existentes = new Set(tareas.filter(t => t.casaId === casaId).map(t => t.nombre.trim().toLowerCase()));
  const disponibles = TAREAS_PLANTILLA.filter(t => !existentes.has(t.nombre.toLowerCase()));
  const [elegidas, setElegidas] = useState<Set<string>>(
    () => new Set(disponibles.filter(t => t.basica).map(t => t.nombre)));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const alternar = (nombre: string) => setElegidas(prev => {
    const s = new Set(prev);
    if (s.has(nombre)) s.delete(nombre); else s.add(nombre);
    return s;
  });

  const crear = async () => {
    const lista = disponibles.filter(t => elegidas.has(t.nombre));
    if (!lista.length) { onClose(); return; }
    setGuardando(true);
    setError('');
    try {
      // El mantenimiento no cae todo hoy: se reparte en las próximas semanas
      let n = 0;
      await crearTareas(casaId, lista.map(t => ({
        nombre: t.nombre, icono: t.icono, zona: t.zona, repeticion: t.repeticion,
        fecha: t.mantenimiento ? addDays(primeraFecha(t.repeticion), 3 + 4 * n++) : primeraFecha(t.repeticion),
        esfuerzo: t.esfuerzo,
      })));
      vibrar.exito();
      onClose();
    } catch (e) {
      if (ofrecerSiEsLimite(e)) { onClose(); return; }
      console.error(e);
      setError('No se pudieron crear. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  return (
    <ModalBase titulo="Tareas habituales" onClose={onClose}>
      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Marca las que hacéis en casa. Se crean sin asignar: después decides a quién le toca cada una y cambias los días.
        </p>
        <MensajeError texto={error} />

        {casas.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {casas.map(c => (
              <button key={c.id} type="button" onClick={() => setCasaId(c.id)} className={chip(c.id === casaId)}>
                {c.icono} {c.nombre}
              </button>
            ))}
          </div>
        )}

        {disponibles.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Ya tienes todas las de la plantilla en esta casa.</p>
        ) : [
          { titulo: 'Del día a día', lista: disponibles.filter(t => !t.mantenimiento) },
          { titulo: 'Mantenimiento de la casa', ayuda: 'Lo que toca cada pocos meses o una vez al año y se olvida. Se reparten en las próximas semanas; luego cada una vuelve a su ritmo.', lista: disponibles.filter(t => t.mantenimiento) },
        ].filter(g => g.lista.length).map(g => (
          <div key={g.titulo}>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">{g.titulo}</h3>
            {g.ayuda && <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{g.ayuda}</p>}
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700">
              {g.lista.map(t => {
                const marcada = elegidas.has(t.nombre);
                return (
                  <li key={t.nombre}>
                    <button type="button" onClick={() => alternar(t.nombre)} aria-pressed={marcada}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                      <span className={`w-5 h-5 shrink-0 rounded-md border flex items-center justify-center ${
                        marcada ? 'bg-lumbre-600 border-lumbre-600 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                        {marcada && <Check className="w-3.5 h-3.5" />}
                      </span>
                      <span className="text-xl" aria-hidden>{t.icono}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-white">{t.nombre}</span>
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                          {describirRepeticion(t.repeticion)} · {t.zona}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="flex gap-2 pt-1">
          <button onClick={crear} disabled={guardando || elegidas.size === 0} className={`flex-1 py-2.5 ${botonPrincipal}`}>
            {guardando ? 'Creando…' : `Crear ${disponibles.filter(t => elegidas.has(t.nombre)).length} tareas`}
          </button>
          <button onClick={onClose} className={`px-5 py-2.5 ${botonSecundario}`}>Cancelar</button>
        </div>
      </div>
    </ModalBase>
  );
};
